import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import libsodium from 'libsodium-wrappers-sumo';

import {
  authApi,
  SESSION_LIFETIME_OPTIONS,
  type SessionLifetime,
} from '@/api/auth';
import { ApiError } from '@/api/client';
import { readSessionLifetime, writeSessionLifetime } from '@/stores/sessionPref';
import {
  bytesToBase64,
  computeAuthHash,
  deriveMasterKey,
  ensureSodium,
  generateKeyPair,
  generateSalt,
  encryptSym,
} from '@/crypto/sodium';
import { acceptInviteAndSync } from '@/features/auth/acceptInvite';
import { useAuthStore } from '@/stores/auth';
import RecoveryPhraseCard from '@/components/RecoveryPhraseCard';
import { buildRecoveryBundle, generateRecoveryPhrase } from '@/crypto/recovery';
import { readInviteToken } from '@/lib/inviteToken';

const MIN_PASSWORD_LEN = 12;

// Parametrii KDF de referință — trebuie să corespundă celor pe care îi
// trimitem la backend. mem_kib * 1024 = memlimit în bytes.
const DEFAULT_PARAMS = {
  algorithm: 'argon2id' as const,
  mem_kib: 65536, // 64 MiB
  iters: 3,
  parallelism: 1,
  key_len: 32,
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [params] = useSearchParams();
  const inviteToken = readInviteToken('invite');
  const presetEmail = params.get('email') ?? '';

  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [lifetime, setLifetime] = useState<SessionLifetime>(() => readSessionLifetime());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Setat după crearea contului: fraza de arătat și unde mergem apoi.
  const [issued, setIssued] = useState<{ phrase: string; next: string } | null>(null);

  useEffect(() => {
    setEmail(presetEmail);
  }, [presetEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LEN) {
      setError(t('recovery.password_too_short', { n: MIN_PASSWORD_LEN }));
      return;
    }
    if (password !== confirm) {
      setError(t('shell_ui.register.passwords_mismatch'));
      return;
    }

    setBusy(true);
    try {
      await ensureSodium();

      const salt = await generateSalt();
      const masterKey = await deriveMasterKey(password, salt, {
        opslimit: DEFAULT_PARAMS.iters,
        memlimit: DEFAULT_PARAMS.mem_kib * 1024,
      });
      const authHash = await computeAuthHash(masterKey);
      const keyPair = await generateKeyPair();
      const sealedPriv = await encryptSym(keyPair.privateKey, masterKey);

      // Fraza de recuperare se generează automat, odată cu contul.
      const recoveryPhrase = generateRecoveryPhrase();
      const recovery = await buildRecoveryBundle(recoveryPhrase, keyPair);

      const result = await authApi.register({
        email: email.trim().toLowerCase(),
        kdf_params: DEFAULT_PARAMS,
        kdf_salt: bytesToBase64(salt),
        auth_hash: bytesToBase64(authHash),
        public_key: bytesToBase64(keyPair.publicKey),
        private_key_encrypted: bytesToBase64(sealedPriv.ciphertext),
        private_key_nonce: bytesToBase64(sealedPriv.nonce),
        tenant_name: tenantName.trim() || undefined,
        session_lifetime_minutes: lifetime,
        recovery,
      });
      writeSessionLifetime(lifetime);

      // Ștergem auth_hash local — nu mai ne trebuie.
      libsodium.memzero(authHash);

      setSession(
        {
          id: result.user_id,
          email: email.trim().toLowerCase(),
          tenants: [
            {
              tenant_id: result.tenant_id,
              name: tenantName.trim() || `${email.split('@')[0]}'s workspace`,
              slug: '',
              role: 'owner',
            },
          ],
        },
        masterKey,
        keyPair,
        result.session_expires_at,
      );

      // Contul există; înainte de a intra în aplicație arătăm fraza.
      const next = '/vault';
      if (inviteToken) {
        try {
          await acceptInviteAndSync(inviteToken);
        } catch {
          // continuă oricum — contul e creat, userul poate accepta manual
        }
      }
      setIssued({ phrase: recoveryPhrase, next });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else if (err instanceof Error) {
        setError(t('shell_ui.register.crypto_error', { message: err.message }));
        console.error('register crypto error', err);
      } else {
        setError(t('shell_ui.register.unexpected_error', { message: String(err) }));
      }
    } finally {
      setBusy(false);
    }
  };

  if (issued) {
    return (
      <div className="app-main-bg min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="card space-y-4" data-testid="register-recovery">
            <div>
              <h2 className="display-title-sm mb-1">{t('recovery.register_title')}</h2>
              <p className="text-[13px]" style={{ color: 'var(--text-muted)' }}>
                {t('recovery.register_description')}
              </p>
            </div>
            <RecoveryPhraseCard phrase={issued.phrase} email={email.trim().toLowerCase()} />
            <p className="text-xs" style={{ color: 'var(--text-dim)' }}>
              {t('recovery.register_later')}
            </p>
            <button
              type="button"
              className="btn-primary w-full"
              data-testid="register-continue"
              onClick={() => navigate(issued.next, { replace: true })}
            >
              {t('recovery.register_continue')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-main-bg min-h-screen flex items-center justify-center p-4"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="display-title-lg">{t('app.name')}</h1>
          <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>
            {t('shell_ui.register.tagline')}
          </p>
        </div>

        <div className="card">
          <h2 className="display-title-sm mb-1">{t('auth.register_title')}</h2>
          <p className="text-[13px] mb-6" style={{ color: 'var(--text-muted)' }}>
            {t('shell_ui.register.description')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="register-page-1" className="section-label no-dot mb-1.5">{t('auth.email')}</label>
              <input id="register-page-1"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder={t('shell_ui.register.email_placeholder')}
                autoComplete="email"
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="register-page-2" className="section-label no-dot mb-1.5">{t('shell_ui.register.space_name_label')}</label>
              <input id="register-page-2"
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="input"
                placeholder={t('shell_ui.register.space_name_placeholder')}
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="register-page-3" className="section-label no-dot mb-1.5">{t('shell_ui.register.password_label')}</label>
              <input id="register-page-3"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input font-mono"
                placeholder={t('shell_ui.register.password_placeholder', { n: MIN_PASSWORD_LEN })}
                autoComplete="new-password"
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="register-page-4" className="section-label no-dot mb-1.5">{t('shell_ui.register.confirm_label')}</label>
              <input id="register-page-4"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input font-mono"
                autoComplete="new-password"
                disabled={busy}
              />
            </div>

            <div>
              <label htmlFor="register-page-5" className="section-label no-dot mb-1.5">{t('auth.session_duration')}</label>
              <select id="register-page-5"
                className="input"
                value={lifetime}
                onChange={(e) => setLifetime(Number(e.target.value) as SessionLifetime)}
                disabled={busy}
              >
                {SESSION_LIFETIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                className="text-[13px] rounded px-3 py-2"
                style={{
                  color: 'var(--danger)',
                  background: 'var(--danger-bg)',
                  border: '1px solid var(--danger)',
                }}
              >
                {error}
              </div>
            )}

            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? t('auth.login_deriving') : t('shell_ui.register.submit')}
            </button>
          </form>

          <div
            className="mt-6 pt-4 text-xs text-center"
            style={{ borderTop: '1px solid var(--border)', color: 'var(--text-dim)' }}
          >
            {t('shell_ui.register.have_account')}{' '}
            <Link to="/login" style={{ color: 'var(--accent)' }} className="hover:underline">
              {t('shell_ui.register.login_link')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
